// Utils

import Dictionary from '@/types/dictionary';
import { DISCLAIMER_KEYS } from './constants';
import helpers from './helpers';
import Disclaimer from '@/types/disclaimer';
import Stock from '@/types/stock';
import { formatMessage } from '../i18n';

/**
 * Generates the disclaimers to be used inside a comparison for a stock.
 *
 * @param stock Stock used to generate the disclaimers.
 */

export const getComparisonStockDisclaimers = (stock: Stock) => {
  const disclaimers: Dictionary<Disclaimer> = {};
  const firstDataDate = helpers.getFirstDataPointDate(stock);
  const lastDataDate = helpers.getLastDataPointDate(stock);
  const lastNavDataDate = helpers.getLastNavDataPointDate(stock);

  // Generic disclaimer with the date of the first data point -- used for time-based data fields.
  disclaimers[DISCLAIMER_KEYS.STOCK_NO_DATA_BEFORE_DATE] = {
    key: DISCLAIMER_KEYS.STOCK_NO_DATA_BEFORE_DATE,
    data: {
      date: firstDataDate,
    },
  };

  // Disclaimers related to missing data
  if (helpers.holdingIsClosed(stock)) {
    disclaimers[DISCLAIMER_KEYS.STOCK_CLOSED] = {
      key: DISCLAIMER_KEYS.STOCK_CLOSED,
      data: {
        date: lastDataDate,
      },
    };
  }

  if (helpers.holdingNavIsClosed(stock)) {
    disclaimers[DISCLAIMER_KEYS.STOCK_NAV_CLOSED] = {
      key: DISCLAIMER_KEYS.STOCK_NAV_CLOSED,
      data: {
        date: lastNavDataDate,
      },
    };
  }

  if (helpers.holdingHasNoData(stock)) {
    disclaimers[DISCLAIMER_KEYS.STOCK_NO_DATA] = {
      key: DISCLAIMER_KEYS.STOCK_NO_DATA,
    };

    disclaimers[DISCLAIMER_KEYS.STOCK_NO_RISK_METRICS] = {
      key: DISCLAIMER_KEYS.STOCK_NO_RISK_METRICS,
    };
  }

  if (helpers.holdingHasNoNavData(stock)) {
    disclaimers[DISCLAIMER_KEYS.STOCK_NO_NAV_DATA] = {
      key: DISCLAIMER_KEYS.STOCK_NO_NAV_DATA,
    };
  }

  if (helpers.holdingHasLessThan1YData(stock)) {
    disclaimers[DISCLAIMER_KEYS.STOCK_LESS_THAN_1Y] = {
      key: DISCLAIMER_KEYS.STOCK_LESS_THAN_1Y,
      data: {
        date: firstDataDate,
      },
    };

    disclaimers[DISCLAIMER_KEYS.STOCK_NO_RISK_METRICS] = {
      key: DISCLAIMER_KEYS.STOCK_NO_RISK_METRICS,
    };
  }

  if (helpers.holdingHasLessThan3YData(stock)) {
    disclaimers[DISCLAIMER_KEYS.STOCK_LESS_THAN_3Y] = {
      key: DISCLAIMER_KEYS.STOCK_LESS_THAN_3Y,
      data: {
        date: firstDataDate,
      },
    };
  }

  disclaimers[DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA] = {
    key: DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
  };

  stock.customProviders?.forEach(provider => {
    if (provider.fields.includes('riskRating')) {
      disclaimers[DISCLAIMER_KEYS.CUSTOM_PROVIDER_RISK_RATING] = {
        key: DISCLAIMER_KEYS.CUSTOM_PROVIDER_RISK_RATING,
        data: {
          provider: formatMessage(`Providers.${provider.key}`),
        },
      };
    }
  });

  return disclaimers;
};
